import { ChangeDetectionStrategy, signal, inject, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';
import { LoggerService } from '@/core/services/logger.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { Card } from 'primeng/card';
import { PessoaDetalhe } from '@/shared/components/pessoa/pessoa-detalhe/pessoa-detalhe';
import { FormsModule } from '@angular/forms';
import { DadosRegistro } from './components/dados-registro/dados-registro';
import { Biometria } from './components/biometria/biometria';
import { Documentos } from './components/documentos/documentos';
import { FormacaoComponent, FormacaoListItem } from './components/formacao/formacao.component';
import { EspecialidadeContainer } from './components/especialidade/especialidade-container';

import { Historico } from './components/historico/historico';
import { Funcoes } from './components/funcoes/funcoes';
import { DashboardConsultorio } from './components/consultorio-privado/dashboard-consultorio';
import { TelemedicinaComponent } from './components/telemedicina/telemedicina';
import { Pcmso } from './components/pcmso/pcmso';
import { ConflitoInteresseComponent } from './components/conflito-interesse/conflito-interesse';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, firstValueFrom, of, Subscription, switchMap } from 'rxjs';
import { Contato } from '@/shared/components/contato/contato';
import { PessoaService as PessoaApiService } from '@/shared/components/pessoa/pessoa-detalhe/pessoa.service';
import { PessoaSaveRequest } from '@/shared/models/pessoa-save-request.model';
import { MedicoService } from '@/core/api/sicom-api/registro/medico/medico.service';
import { MedicoDeficienciaRequest, MedicoPeriodoSuspensao, MedicoRequest } from '@/core/api/sicom-api/registro/medico/medico.model';
import { VistoTemporarioSolicitacaoService } from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.service';
import type { VistoTemporarioSolicitacaoCadastroRequest, VistoTemporarioSolicitacaoCadastroResponse } from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.model';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import type { UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { AuthService } from '@/core/auth/auth.service';
import { MessageService } from 'primeng/api';
import { Anotacoes } from '@/shared/components/anotacoes/anotacoes';
import { CustomClass } from '@/shared/classes/custom.class';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { Empty } from '@/shared/components/layout/empty/empty';
import { FormacaoListaComponent } from './components/formacao/formacao-lista/formacao-lista.component';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { normalizarTipoPessoa as normalizarTipoBasico } from '@/shared/utils/pessoa-tipo.util';
import { DateUtils } from '@/shared/utils/date-utils';
import { GerenciarVistoComponent } from '../solicitacoes/visto-temporario/gerenciar-visto';
import { LocalizarVistoComponent } from '../solicitacoes/visto-temporario/localizar-visto';
import { NovoVistoComponent, NovoVistoSolicitacao } from '../solicitacoes/visto-temporario/novo-visto';
import type { VistoTemporarioProtocoloSelecionado } from '../solicitacoes/visto-temporario/visto-temporario.types';
import { AbertasUfOrigemComponent } from '../solicitacoes/central-transferencia/abertas-uf-origem';
import { AbertasUfDestinoComponent } from '../solicitacoes/central-transferencia/abertas-uf-destino';
import { AguardandoEncerramentoComponent } from '../solicitacoes/central-transferencia/aguardando-encerramento';
import { SolicitarTransferenciaComponent } from '../solicitacoes/central-transferencia/solicitar-transferencia';
import { LocalizarPorCpfComponent } from '../solicitacoes/central-transferencia/localizar-por-cpf';
import { DocumentosMedico } from '../solicitacoes/documentos/documentos-medico';
import { DadosRegistroFormValue, DocumentosFormValue } from './medicos-form.types';

@Component({
    selector: 'app-dashboard-medicos',
    imports: [
        Card,
        PessoaDetalhe,
        FormsModule,
        DadosRegistro,
        Biometria,
        Documentos,
        FormacaoComponent,
        EspecialidadeContainer,
        Historico,
        Funcoes,
        Anotacoes,
        DashboardConsultorio,
        TelemedicinaComponent,
        Pcmso,
        ConflitoInteresseComponent,
        Contato,
        Anotacoes,
        Pesquisa,
        Empty,
        FormacaoListaComponent,
        GerenciarVistoComponent,
        LocalizarVistoComponent,
        NovoVistoComponent,
        AbertasUfOrigemComponent,
        AbertasUfDestinoComponent,
        AguardandoEncerramentoComponent,
        SolicitarTransferenciaComponent,
        LocalizarPorCpfComponent,
        DocumentosMedico
    ],
    templateUrl: './dashboard-medicos.html',
    styleUrl: './dashboard-medicos.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Container do módulo de médicos: seleciona a pessoa e alterna entre abas/ferramentas. */
export class DashboardMedicos extends CustomClass implements OnInit, OnDestroy {
    protected readonly PERMISSOES = PERMISSOES;
    private readonly logger = inject(LoggerService);
    private readonly permissionService = inject(PermissionService);
    private readonly componentesFerramentas = new Set([
        'visto-temporario',
        'visto-temporario-localizar',
        'visto-temporario-novo',
        'central-transferencias',
        'central-transferencias-solicitar',
        'central-transferencias-localizar-cpf',
        'central-transferencias-abertas-uf-origem',
        'central-transferencias-abertas-uf-destino',
        'central-transferencias-aguardando-encerramento'
    ]);
    private routeSub: Subscription | undefined;
    pessoaDetalheComponent = viewChild<PessoaDetalhe>('pessoaDetalhe');
    dadosRegistroComponent = viewChild<DadosRegistro>('dadosRegistroRef');
    documentosComponent = viewChild<Documentos>('documentosRef');
    pesquisarDialog = false;
    modoEdicao = false;
    vistoAcaoSelecionada: 'LOCALIZAR' | 'NOVO' | null = null;
    mostrarLocalizarVisto = false;
    mostrarNovoVisto = false;
    mostrarGerenciarVisto = false;
    novoVistoSolicitacao: NovoVistoSolicitacao | null = null;
    dadosLocalizarVisto: VistoTemporarioProtocoloSelecionado | null = null;
    cadastroVistoEmAndamento = false;

    constructor(
        private route: ActivatedRoute,
        private medicoService: MedicoService,
        private messageService: MessageService,
        private pessoaApiService: PessoaApiService,
        private vistoTemporarioSolicitacaoService: VistoTemporarioSolicitacaoService,
        private usuarioService: UsuarioService,
        private authService: AuthService
    ) {
        super();
    }
    selectedComponent = signal('');
    formacaoDetailData: FormacaoListItem | null = null;
    loadData = this.carregarPessoa.bind(this);

    ngOnInit(): void {
        // A rota estática declara `data: { codigo }`; o fallback para paramMap mantém
        // compatibilidade com links antigos no formato `item/:codigo`.
        this.routeSub = combineLatest([this.route.data, this.route.paramMap]).subscribe(([data, params]) => {
            // A raiz `/medicos` não é gateada (é a tela de busca) — só as 21 rotas `item/<slug>`
            // têm `permissionGuard`. Por isso o default da raiz (quando não vem slug algum) não
            // pode assumir 'dados-registro' cegamente: precisa checar a mesma permissão que a
            // rota `item/dados-registro` exige, senão contorna o guard daquela aba.
            const slug = (data['codigo'] as string | undefined) ?? params.get('codigo');
            const codigo = slug ?? this.defaultDaRaiz();
            this.selectedComponent.set(codigo === 'visto-temporario' ? 'visto-temporario-localizar' : codigo);
            this.vistoAcaoSelecionada = null;
            this.mostrarLocalizarVisto = this.selectedComponent() === 'visto-temporario-localizar';
            this.mostrarNovoVisto = this.selectedComponent() === 'visto-temporario-novo';
            this.mostrarGerenciarVisto = false;
            this.novoVistoSolicitacao = null;
            this.dadosLocalizarVisto = null;
        });
        this.loadData(this.pessoa());

        // Após um reload, a pessoa vem do sessionStorage apenas como {id, uf, tipo} — sem nome nem
        // registro, que são PII deliberadamente não persistida (ver pessoa.service.ts). Sem esta
        // chamada o campo Nome fica vazio (bloqueando o Salvar) e o CPF não é enviado ao backend,
        // que recusa a gravação. O enriquecimento vem da API, nunca do storage.
        this.refreshPessoaDoContainer();
    }

    ngOnDestroy(): void {
        // É uma boa prática cancelar a inscrição para evitar vazamento de memória
        this.routeSub!.unsubscribe();
    }

    isMedicoSelecionado(): boolean {
        const pessoaAtual = this.pessoa();
        const tipoNormalizado = normalizarTipoBasico(pessoaAtual?.tipo);

        if (tipoNormalizado) {
            // Pré-Médico é categoria própria e NÃO carrega aqui.
            return tipoNormalizado === 'MEDICO';
        }

        // Fallback defensivo: se o tipo vier ausente após salvar,
        // mantém o contexto da pessoa selecionada para não perder a tela atual.
        return this.temPessoaSelecionada(pessoaAtual);
    }

    isFerramentaSelecionada(): boolean {
        return this.componentesFerramentas.has(this.selectedComponent());
    }

    podeAcessarComponenteSelecionado(): boolean {
        return this.isFerramentaSelecionada() || this.isMedicoSelecionado();
    }

    /**
     * Default de aba quando a raiz `/medicos` não recebeu slug algum (nem `data.codigo`,
     * nem `paramMap.codigo`). Só cai em 'dados-registro' se o usuário tiver a mesma
     * permissão exigida pela rota `item/dados-registro` — do contrário, retorna o
     * sentinela vazio, que o template trata como "nenhuma aba selecionada".
     */
    private defaultDaRaiz(): string {
        return this.permissionService.can(PERMISSOES.medicos.dadosRegistro.escopo) ? 'dados-registro' : '';
    }

    /**
     * Chave de idempotência por aba de edição. `PUT /medico` encadeia seis pós-salvar, um deles
     * gravando histórico por max + 1 — sem chave, o reenvio deixa histórico duplicado.
     *
     * Gira no SUCESSO, não a cada envio. Assim: gravação recusada mantém a chave, e a correção
     * reenviada é reconhecida como a mesma intenção (o backend liberou a chave no 4xx); gravação
     * concluída começa uma sessão nova. Girar a cada envio não protegeria nada, e nunca girar
     * deixaria o usuário salvar uma única vez por TTL.
     */
    private chaveDadosRegistro = novaChaveIdempotencia();
    private chaveDocumentos = novaChaveIdempotencia();

    onSalvarDadosRegistro(evt: { pessoaPartial: Partial<PessoaSaveRequest>; medicoFormValue: DadosRegistroFormValue }): void {
        if (!this.pessoa || typeof this.pessoa()?.id !== 'number' || typeof this.pessoa()?.uf !== 'string') {
            this.logger.warn('Pessoa (id/uf) não informada no container. Não é possível salvar.');
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pessoa não informada. Não é possível salvar.' });
            return;
        }
        const cpfOuCnpj = this.resolveCpfOuCnpj(evt?.pessoaPartial?.cpfOuCnpj);
        const payload: PessoaSaveRequest = {
            id: this.pessoa()?.id,
            uf: this.pessoa()?.uf,
            ...evt.pessoaPartial,
            // Sempre enviar CPF/CNPJ conforme requisito
            cpfOuCnpj
        };
        const reqPessoa = { id: this.pessoa()?.id, uf: this.pessoa()?.uf } as Pessoa;
        this.pessoaApiService
            .salvar(payload)
            .pipe(
                // Após salvar a pessoa, buscar o médico atual para compor o payload completo
                switchMap(() => this.medicoService.buscar(reqPessoa)),
                switchMap((baseMedico: MedicoRequest) => {
                    const patch = this.mapDadosRegistroToMedicoPatch(evt.medicoFormValue);
                    const merged: MedicoRequest = {
                        ...baseMedico,
                        ...patch,
                        // Identificadores exigidos pelo backend (PessoaInfoRequest)
                        id: this.pessoa()!.id,
                        uf: this.pessoa()!.uf,
                        // Garantir os identificadores
                        ufSgUf: this.pessoa()!.uf,
                        idSolicitante: this.pessoa()!.id
                    } as MedicoRequest;
                    return this.medicoService.atualizar(merged, comIdempotencia(this.chaveDadosRegistro));
                }),
                switchMap(() => this.salvarDeficienciaSeNecessario(evt.medicoFormValue))
            )
            .subscribe({
                next: () => {
                    // Sessão encerrada: a próxima gravação é outra intenção e precisa de chave nova.
                    this.chaveDadosRegistro = novaChaveIdempotencia();
                    this.dadosRegistroComponent()?.cancelarEdicao();
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Dados de registro salvos com sucesso.' });
                    // this.pessoa()?.reload();
                    this.refreshPessoaDoContainer();
                },
                error: (err) => {
                    this.logger.error('Erro ao salvar Pessoa e/ou atualizar Médico (dados-registro):', err);
                    this.exibirErroApi(err, 'dados-registro');
                }
            });
    }

    onSalvarDocumentos(evt: { pessoaPartial: Partial<PessoaSaveRequest>; documentosFormValue: DocumentosFormValue }): void {
        if (!this.pessoa() || typeof this.pessoa()?.id !== 'number' || typeof this.pessoa()?.uf !== 'string') {
            this.logger.warn('Pessoa (id/uf) não informada no container. Não é possível salvar.');
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pessoa não informada. Não é possível salvar.' });
            return;
        }
        const cpfOuCnpj = this.resolveCpfOuCnpj(evt?.pessoaPartial?.cpfOuCnpj);
        const payload: PessoaSaveRequest = {
            id: this.pessoa()?.id,
            uf: this.pessoa()?.uf,
            ...evt.pessoaPartial,
            // Sempre enviar CPF/CNPJ conforme requisito
            cpfOuCnpj
        };
        const reqPessoa = { id: this.pessoa()?.id, uf: this.pessoa()?.uf } as Pessoa;
        this.pessoaApiService
            .salvar(payload)
            .pipe(
                switchMap(() => this.medicoService.buscar(reqPessoa)),
                switchMap((baseMedico: MedicoRequest) => {
                    const patch = this.mapDocumentosToMedicoPatch(evt.documentosFormValue);
                    const merged: MedicoRequest = {
                        ...baseMedico,
                        ...patch,
                        // Identificadores exigidos pelo backend (PessoaInfoRequest)
                        id: this.pessoa()!.id,
                        uf: this.pessoa()!.uf,
                        ufSgUf: this.pessoa()!.uf,
                        idSolicitante: this.pessoa()!.id
                    } as MedicoRequest;
                    return this.medicoService.atualizar(merged, comIdempotencia(this.chaveDocumentos));
                })
            )
            .subscribe({
                next: () => {
                    // Sessão encerrada: a próxima gravação é outra intenção e precisa de chave nova.
                    this.chaveDocumentos = novaChaveIdempotencia();
                    this.documentosComponent()?.cancelarEdicao();
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Documentos salvos com sucesso.' });
                    // this.pessoaDetalhe?.reload();
                    this.refreshPessoaDoContainer();
                },
                error: (err) => {
                    this.logger.error('Erro ao salvar Pessoa e/ou atualizar Médico (documentos):', err);
                    this.exibirErroApi(err, 'documentos');
                }
            });
    }

    /** Ao salvar a biometria, recarrega o card da pessoa (a foto exibida pode ter sido alterada). */
    onBiometriaSalva(): void {
        this.refreshPessoaDoContainer();
    }

    private resolveCpfOuCnpj(preferido?: string): string | undefined {
        // Prioriza o valor recebido do evento (ex.: tela de Documentos)
        const pessoaComDoc = this.pessoa() as (Pessoa & { cpf?: string; cnpj?: string }) | undefined;
        const candidato = preferido ?? pessoaComDoc?.registro ?? pessoaComDoc?.cpf ?? pessoaComDoc?.cnpj;

        if (typeof candidato !== 'string') return undefined;
        // Normaliza removendo qualquer máscara
        const somenteDigitos = candidato.replace(/\D+/g, '');
        return somenteDigitos || undefined;
    }

    /**
     * Após salvar, refaz o GET da pessoa e atualiza o objeto do container + sessionStorage,
     * propagando nome/nomeSocial/registro para os filhos reativos.
     */
    private refreshPessoaDoContainer(): void {
        if (!this.pessoa || typeof this.pessoa()?.id !== 'number' || typeof this.pessoa()?.uf !== 'string') return;
        const id = this.pessoa()?.id;
        const uf = this.pessoa()?.uf;
        if (id != null && uf != null) {
            const req = { id, uf };
            this.pessoaApiService.carregar(req).subscribe({
                next: (resp) => {
                    // Tenta extrair campos simples do payload de resposta
                    const respParsed = resp as Partial<{
                        nomes: Record<string, string | undefined>;
                        dadosGerais: Record<string, string | undefined>;
                        nome: string;
                        nomeSocial: string;
                        registro: string;
                    }>;
                    const nomes = respParsed?.nomes ?? {};
                    const dadosGerais = respParsed?.dadosGerais ?? {};

                    const nome = nomes['nome'] ?? nomes['Nome'] ?? respParsed?.nome;
                    const nomeSocial = nomes['nomeSocial'] ?? nomes['NomeSocial'] ?? nomes['Nome Social'] ?? respParsed?.nomeSocial;
                    const registroBruto = dadosGerais['registro'] ?? dadosGerais['Registro'] ?? respParsed?.registro;
                    const registro = typeof registroBruto === 'string' ? registroBruto : undefined;
                    const pessoaBefore = this.pessoa();

                    if (pessoaBefore) {
                        const tipoAtualizado = this.resolverTipoPessoa(resp?.tipo, pessoaBefore.tipo);
                        const pessoaAtualizada = {
                            ...pessoaBefore,
                            nome: this.resolverCampoTexto(nome, pessoaBefore.nome),
                            nomeSocial: this.resolverCampoTextoPermiteVazio(nomeSocial, pessoaBefore.nomeSocial),
                            registro: this.resolverCampoTexto(registro, pessoaBefore.registro),
                            tipo: tipoAtualizado
                        } satisfies PessoaDomain;

                        this.pessoaService.setPessoa(pessoaAtualizada);
                        this.pessoaDetalheComponent()?.reload();
                    }
                },
                error: (err) => this.logger.warn('Falha ao recarregar pessoa após salvar:', err)
            });
        }
    }

    // Helpers para preservar dados essenciais da pessoa no refresh pós-salvamento.
    private resolverTipoPessoa(tipoApi?: string, tipoAtual?: string): string | undefined {
        const tipoApiNormalizado = this.normalizarTipoPessoa(tipoApi);
        if (this.isTipoPessoaReconhecido(tipoApiNormalizado)) return tipoApiNormalizado;

        const tipoAtualNormalizado = this.normalizarTipoPessoa(tipoAtual);
        if (this.isTipoPessoaReconhecido(tipoAtualNormalizado)) return tipoAtualNormalizado;

        return undefined;
    }

    private resolverCampoTexto(valorApi: unknown, valorAtual?: string): string | undefined {
        if (typeof valorApi !== 'string') return valorAtual;
        const valorNormalizado = valorApi.trim();
        return valorNormalizado ? valorNormalizado : valorAtual;
    }

    private resolverCampoTextoPermiteVazio(valorApi: unknown, valorAtual?: string): string | undefined {
        if (typeof valorApi !== 'string') return valorAtual;
        return valorApi.trim();
    }

    private normalizarTipoPessoa(tipo?: string): string | undefined {
        if (typeof tipo !== 'string') return undefined;
        const valor = tipo
            .trim()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase()
            .replace(/[^A-Z]/g, '');

        return valor || undefined;
    }

    private isTipoPessoaReconhecido(tipo?: string): boolean {
        return tipo === 'MEDICO' || tipo === 'PREMEDICO' || tipo === 'PRESTADOR' || tipo === 'PREPRESTADOR';
    }

    private temPessoaSelecionada(pessoa?: PessoaDomain): boolean {
        return typeof pessoa?.id === 'number' && typeof pessoa?.uf === 'string' && pessoa.uf.trim().length > 0;
    }

    // Campos de domínio fechado (inSexo, inSituacao, inTipoInscricao, inEstrangeiro, inCategoria,
    // inFatorRh) ganharam @Pattern no backend. @Pattern aceita ausência/null do campo, mas rejeita
    // string vazia. O formulário emite '' quando o campo não foi preenchido (ex.: Fator RH em
    // branco) — sem este guard, o patch repassava '' e o backend respondia 400. Omitir a chave do
    // patch quando o valor vier vazio (em vez de enviar null) mantém o tipo de MedicoRequest sem
    // cast e é consistente com o padrão já usado por inSituacao nesta mesma função.
    private valorDominioFechado(valor: string | undefined): string | undefined {
        return valor === '' ? undefined : valor;
    }

    // Mapeia os campos do formulário de Dados Registro para propriedades do MedicoRequest
    private mapDadosRegistroToMedicoPatch(form: DadosRegistroFormValue | null | undefined): Partial<MedicoRequest> {
        if (!form) return {};
        const patch: Partial<MedicoRequest> = {};
        if (form.nomePai !== undefined) patch.nmPai = form.nomePai || '';
        if (form.nomeMae !== undefined) patch.nmMae = form.nomeMae || '';
        if (form.genero !== undefined) {
            const inSexo = this.valorDominioFechado(form.genero);
            if (inSexo !== undefined) patch.inSexo = inSexo;
        }
        if (form.estadoCivil !== undefined) patch.inEstadoCivil = form.estadoCivil;
        if (form.nascimentoData !== undefined) patch.dtNascimento = form.nascimentoData || '';
        if (form.paisIdPais !== undefined && form.paisIdPais !== null && form.paisIdPais !== '') patch.paisIdPais = Number(form.paisIdPais);
        // Município Exterior (quando país != Brasil). Enviar mesmo vazio para limpar no backend, se necessário
        if (form.nmMunicipioExterior !== undefined) {
            patch.nmMunicipioExterior = form.nmMunicipioExterior || '';
        }
        if (form.inCertidaoEtica !== undefined) {
            patch.inCertidaoEtica = form.inCertidaoEtica; // já vem como 'S' | 'N' do componente
        } else if (form.emitirCertEtica !== undefined) {
            patch.inCertidaoEtica = form.emitirCertEtica ? 'S' : 'N';
        }
        if (form.inEstrangeiro !== undefined) {
            const inEstrangeiro = this.valorDominioFechado(form.inEstrangeiro); // esperado 'B' | 'E' | 'N'
            if (inEstrangeiro !== undefined) patch.inEstrangeiro = inEstrangeiro;
        } else if (form.nacionalidade !== undefined) {
            const inEstrangeiro = this.valorDominioFechado(form.nacionalidade);
            if (inEstrangeiro !== undefined) patch.inEstrangeiro = inEstrangeiro;
        }
        // Categoria Militar: utilizar inCategoria ('C' | 'M')
        if (form.inCategoria !== undefined) {
            const inCategoria = this.valorDominioFechado(form.inCategoria);
            if (inCategoria !== undefined) patch.inCategoria = inCategoria;
        } else if (form.categoriaMilitar !== undefined) {
            const inCategoria = this.valorDominioFechado(form.categoriaMilitar);
            if (inCategoria !== undefined) patch.inCategoria = inCategoria;
        }
        if (form.dtValCertificadoMilitar !== undefined || form.decMilitarData !== undefined) {
            const dtValCertificadoMilitar = this.normalizarDataEntrada(form.dtValCertificadoMilitar ?? form.decMilitarData);
            (patch as { dtValCertificadoMilitar?: string | null }).dtValCertificadoMilitar = dtValCertificadoMilitar ?? null;
        }
        // Tipo sanguíneo (ABO)
        if (form.inTipoSangue !== undefined) {
            patch.inTipoSangue = form.inTipoSangue;
        } else if (form.tipoSanguineo !== undefined) {
            patch.inTipoSangue = form.tipoSanguineo;
        }
        // Fator RH: '+' | '-'. @Pattern no backend aceita null mas rejeita '' — nunca emitir vazio.
        if (form.inFatorRh !== undefined) {
            const inFatorRh = this.valorDominioFechado(form.inFatorRh);
            if (inFatorRh !== undefined) patch.inFatorRh = inFatorRh;
        } else if (form.rh !== undefined) {
            const rhVal = form.rh;
            const inFatorRh = this.valorDominioFechado(rhVal === 'POSITIVO' ? '+' : rhVal === 'NEGATIVO' ? '-' : rhVal);
            if (inFatorRh !== undefined) patch.inFatorRh = inFatorRh;
        }
        // Doador de órgãos: 'S' | 'N'
        if (form.inDoador !== undefined) {
            patch.inDoador = form.inDoador;
        } else if (form.doacaoOrgaos !== undefined) {
            const d = form.doacaoOrgaos;
            patch.inDoador = d === 'DOADOR' ? 'S' : d === 'NAO_DOADOR' ? 'N' : d;
        }
        if (form.tipoInscricao !== undefined) {
            const inTipoInscricao = this.valorDominioFechado(form.tipoInscricao);
            if (inTipoInscricao !== undefined) patch.inTipoInscricao = inTipoInscricao;
        }
        if (form.tipoLicenca !== undefined) patch.inTipoLicenca = form.tipoLicenca;
        if (form.situacao !== undefined) {
            const situacao = this.extrairCodigoSituacao(form.situacao);
            if (situacao) patch.inSituacao = situacao;
        }
        const dtMudancaSituacao = this.normalizarDataEntrada(form.dtMudancaSituacao ?? form.dtAlteracaoTipoInscricao);
        if (dtMudancaSituacao) patch.dtMudancaSituacao = dtMudancaSituacao;
        const periodoSuspensao = this.montarPeriodoSuspensao(form);
        if (periodoSuspensao) patch.periodoSuspensao = periodoSuspensao;
        // Município (Naturalidade)
        if (form.municipioIdMunicipio !== undefined && form.municipioIdMunicipio !== null && form.municipioIdMunicipio !== '') {
            patch.municipioIdMunicipio = Number(form.municipioIdMunicipio);
        }
        return patch;
    }

    private salvarDeficienciaSeNecessario(form: DadosRegistroFormValue) {
        const request = this.mapDeficienciaRequest(form);
        if (!request) return of(null);
        return this.medicoService.salvarDeficiencia(request);
    }

    private mapDeficienciaRequest(form: DadosRegistroFormValue): MedicoDeficienciaRequest | null {
        const pessoaAtual = this.pessoa();
        if (!pessoaAtual || typeof pessoaAtual.id !== 'number' || typeof pessoaAtual.uf !== 'string') return null;

        const comDeficiencia = this.normalizarTexto(form?.comDeficiencia);
        if (comDeficiencia !== 'S') return null;

        const tipo = this.normalizarTexto(form?.tipoDeficiencia);
        const origem = this.normalizarTexto(form?.origemDeficiencia);
        const adaptado = this.normalizarTexto(form?.deficienciaAdaptada);
        const outrasInfo = this.normalizarTextoLivre(form?.deficiencias);

        return {
            idSolicitante: pessoaAtual.id,
            sgUf: pessoaAtual.uf,
            inTipoDeficiencia: tipo,
            inOrigemDeficiencia: origem,
            inAdaptadoDeficiencia: adaptado || undefined,
            txOutraDeficiencia: outrasInfo ? outrasInfo.slice(0, 250) : undefined
        };
    }

    private normalizarTexto(valor: unknown): string {
        return (valor ?? '').toString().trim().toUpperCase();
    }

    private normalizarTextoLivre(valor: unknown): string {
        return (valor ?? '').toString().trim();
    }

    private normalizarDataEntrada(valor: unknown): string | undefined {
        const raw = (valor ?? '').toString().trim();
        if (!raw) return undefined;
        if (DateUtils.isDateString(raw)) {
            return DateUtils.formatDate(raw);
        }
        return raw;
    }

    private extrairCodigoSituacao(valor: unknown): string | undefined {
        if (valor == null) return undefined;
        if (typeof valor === 'string') return valor.trim().toUpperCase();
        if (typeof valor === 'number') return String(valor);
        if (typeof valor === 'object') {
            const obj = valor as { value?: unknown; codigo?: unknown; idMedSituacao?: unknown };
            const possible = obj.value ?? obj.codigo ?? obj.idMedSituacao;
            if (possible != null) {
                return String(possible).trim().toUpperCase();
            }
        }
        return undefined;
    }

    private montarPeriodoSuspensao(form: DadosRegistroFormValue): MedicoPeriodoSuspensao | undefined {
        if (!form) return undefined;
        const dtInicial = this.normalizarDataEntrada(form.suspensaoInicio);
        const dtFinal = this.normalizarDataEntrada(form.suspensaoFim);
        const observacao = this.normalizarTextoLivre(form.observacao);

        if (!dtInicial && !dtFinal && !observacao) return undefined;

        return {
            dtInicial: dtInicial || undefined,
            dtFinal: dtFinal || undefined,
            txObservacao: observacao || undefined,
            documentoBase64: form.documentoSuspensaoBase64 || undefined,
            inDivulgaPortal: this.normalizarTexto(form.suspensaoDivulgaPortal) || undefined
        };
    }

    // Mapeia os campos do formulário de Documentos para propriedades do MedicoRequest
    private mapDocumentosToMedicoPatch(form: DocumentosFormValue | null | undefined): Partial<MedicoRequest> {
        if (!form) return {};
        const patch: Partial<MedicoRequest> = {};
        if (form.rgNumero !== undefined) patch.nuRg = form.rgNumero || '';
        if (form.rgDataEmissao !== undefined) patch.dtExpedicaoRg = form.rgDataEmissao || '';
        if (form.rgOrgaoEmissor !== undefined) patch.nmOrgaoExpedidor = form.rgOrgaoEmissor || '';
        if (form.rgUF !== undefined) patch.sgUfRgExpedida = form.rgUF || '';

        if (form.tituloNumero !== undefined) patch.nuTituloEleitor = form.tituloNumero || '';
        if (form.tituloZona !== undefined) patch.dsZona = form.tituloZona || '';
        if (form.tituloSecao !== undefined) patch.dsSecao = form.tituloSecao || '';
        if (form.tituloUF !== undefined) patch.sgUfTituloEleitor = form.tituloUF || '';

        if (form.rneNumero !== undefined) patch.nuCedulaEstrangeira = form.rneNumero || '';
        if (form.rneDataEmissao !== undefined) patch.dtCedulaEstrangeira = form.rneDataEmissao || '';
        if (form.rneDataValidade !== undefined) patch.dtValCedulaEstrangeira = form.rneDataValidade || '';
        if (form.passaporteNumero !== undefined) patch.nuPassaporte = form.passaporteNumero || '';

        if (form.certificadoMilitarNumero !== undefined) patch.nuCertificadoMilitar = form.certificadoMilitarNumero || '';
        if (form.certificadoMilitarCategoria !== undefined) patch.nmRegiao = form.certificadoMilitarCategoria || '';
        if (form.situacaoMilitar !== undefined) patch.inSituacaoMilitar = form.situacaoMilitar || '';
        if (form.ministerio !== undefined) {
            patch.inMinisterio = form.ministerio || '';
            patch.nmMinisterio = this.mapMinisterioCodigoParaNome(form.ministerio) || '';
        }

        if (form.tipoRegistro !== undefined && typeof form.tipoRegistro === 'string') {
            const val = form.tipoRegistro.toUpperCase();
            patch.inEstrProvPerm = val.startsWith('PRO') ? 'V' : 'P'; // PROVISORIO -> 'V', PERMANENTE -> 'P'
        }

        return patch;
    }

    private mapMinisterioCodigoParaNome(codigo?: string): string {
        switch ((codigo ?? '').toUpperCase()) {
            case 'M':
                return 'Marinha';
            case 'A':
                return 'Aeronáutica';
            case 'E':
                return 'Exército';
            default:
                return '';
        }
    }

    private exibirErroApi(err: unknown, contexto: 'dados-registro' | 'documentos'): void {
        // Extrai melhor mensagem possível do erro do backend
        const detalhe = this.extrairMensagemErro(err) || (contexto === 'dados-registro' ? 'Falha ao salvar dados de registro.' : 'Falha ao salvar documentos.');
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: detalhe });
    }

    private extrairMensagemErro(err: unknown): string | undefined {
        if (!err) return undefined;
        const e = err as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }

    showPesquisarPessoa = () => {
        this.pesquisarDialog = true;
    };

    onFormacaoRowClick(event: FormacaoListItem) {
        this.modoEdicao = false;
        this.formacaoDetailData = event;
    }

    onFormacaoNovo(type: 'GRAD' | 'POS') {
        this.modoEdicao = true;
        this.formacaoDetailData = { _type: type, _index: -1, table: undefined };
    }

    onFormacaoVoltar() {
        this.modoEdicao = false;
        this.formacaoDetailData = null;
    }

    confirmarLocalizacaoVisto(dadosSelecionados: VistoTemporarioProtocoloSelecionado): void {
        this.vistoAcaoSelecionada = 'LOCALIZAR';
        this.dadosLocalizarVisto = dadosSelecionados;
        this.mostrarLocalizarVisto = false;
        this.mostrarNovoVisto = false;
        this.mostrarGerenciarVisto = true;
        this.novoVistoSolicitacao = null;
    }

    cancelarLocalizacaoVisto(): void {
        this.mostrarLocalizarVisto = this.selectedComponent() === 'visto-temporario-localizar';
        this.mostrarNovoVisto = false;
        this.mostrarGerenciarVisto = false;
        this.vistoAcaoSelecionada = 'LOCALIZAR';
        this.novoVistoSolicitacao = null;
        this.dadosLocalizarVisto = null;
    }

    async confirmarNovoVistoSolicitacao(solicitacao: NovoVistoSolicitacao): Promise<void> {
        if (this.cadastroVistoEmAndamento) {
            return;
        }

        const payload = await this.montarPayloadCadastroVisto(solicitacao);
        if (!payload) {
            return;
        }

        this.cadastroVistoEmAndamento = true;
        try {
            const protocolo = await firstValueFrom(this.vistoTemporarioSolicitacaoService.cadastrarSolicitacao(payload));
            this.vistoAcaoSelecionada = 'NOVO';
            this.novoVistoSolicitacao = null;
            this.dadosLocalizarVisto = this.montarDadosVistoCriado(solicitacao, protocolo, payload);
            this.mostrarLocalizarVisto = false;
            this.mostrarNovoVisto = false;
            this.mostrarGerenciarVisto = true;
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Solicitacao de visto temporario cadastrada.' });
        } catch (erro) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: this.extrairMensagemErro(erro) ?? 'Nao foi possivel cadastrar a solicitacao de visto temporario.'
            });
        } finally {
            this.cadastroVistoEmAndamento = false;
        }
    }

    cancelarNovoVistoSolicitacao(): void {
        this.mostrarLocalizarVisto = false;
        this.mostrarNovoVisto = this.selectedComponent() === 'visto-temporario-novo';
        this.mostrarGerenciarVisto = false;
        this.vistoAcaoSelecionada = 'NOVO';
        this.novoVistoSolicitacao = null;
        this.dadosLocalizarVisto = null;
    }

    voltarParaOpcoesVisto(): void {
        const voltarParaNovoVisto = this.vistoAcaoSelecionada === 'NOVO' || this.selectedComponent() === 'visto-temporario-novo';
        this.mostrarLocalizarVisto = !voltarParaNovoVisto;
        this.mostrarNovoVisto = voltarParaNovoVisto;
        this.mostrarGerenciarVisto = false;
        this.vistoAcaoSelecionada = voltarParaNovoVisto ? 'NOVO' : 'LOCALIZAR';
        this.novoVistoSolicitacao = null;
        this.dadosLocalizarVisto = null;
    }

    private async montarPayloadCadastroVisto(solicitacao: NovoVistoSolicitacao): Promise<VistoTemporarioSolicitacaoCadastroRequest | null> {
        const contexto = await this.buscarContextoTramitacaoVisto();
        if (!contexto) {
            return null;
        }

        const dataInicioPeriodo = this.converterDataVistoParaIso(solicitacao.periodoInicio);
        const dataFinalPeriodo = this.converterDataVistoParaIso(solicitacao.periodoFim);
        if (!dataInicioPeriodo || !dataFinalPeriodo) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Informe um periodo valido para o visto temporario.' });
            return null;
        }

        const ufSolicitante = this.normalizarUfVisto(solicitacao.solicitanteUf);
        const ufRequerente = this.normalizarUfVisto(solicitacao.requerenteUf);
        const idMedicoRelacionado = solicitacao.medicoRelacionadoId ?? solicitacao.solicitanteId;
        const ufMedicoRelacionado = this.normalizarUfVisto(solicitacao.medicoRelacionadoUf ?? solicitacao.solicitanteUf);
        const ufSelecionadaLocalVisto = this.normalizarUfVisto(solicitacao.ufOrigem);
        const localVisto = solicitacao.localVisto === 'OUTRO_CRM' ? 1 : 0;
        const tipoVisto = solicitacao.tipoVisto === '90_FRACIONADOS' ? 1 : 0;
        const deveVincularInteressado = tipoVisto === 1 && localVisto === 1;
        const ufOrigem = tipoVisto === 0 || localVisto === 1 ? ufSelecionadaLocalVisto : ufSolicitante;
        const ufDestino = tipoVisto === 1 && localVisto === 0 ? ufSelecionadaLocalVisto : contexto.uf;

        if (!ufSolicitante || !ufSelecionadaLocalVisto || !ufOrigem || !ufDestino) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Nao foi possivel identificar as UFs obrigatorias do visto.' });
            return null;
        }

        if (!idMedicoRelacionado || !ufMedicoRelacionado) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Informe o medico relacionado ao periodo do visto temporario.' });
            return null;
        }

        if (deveVincularInteressado && (!solicitacao.requerenteId || !ufRequerente)) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Informe o requerente para visto fracionado de outro CRM.' });
            return null;
        }

        const payload: VistoTemporarioSolicitacaoCadastroRequest = {
            sgUfProtocolo: contexto.uf,
            tipoVisto,
            localVisto,
            observacao: this.valorOpcionalVisto(solicitacao.finalidade),
            documentoOrigem: localVisto === 1 ? this.valorOpcionalVisto(solicitacao.documentoOrigem) : undefined,
            idSolicitante: solicitacao.solicitanteId,
            ufSolicitante,
            tramitacaoInicial: {
                idEoOrigem: contexto.idEstrutura,
                idEoDestino: contexto.idEstrutura,
                idSolicitanteResponsavel: contexto.idSolicitante,
                sgUfResponsavel: contexto.uf,
                idEncaminhador: contexto.idSolicitante,
                sgUfEncaminhador: contexto.uf
            },
            medicos: [
                {
                    idSolicitante: idMedicoRelacionado,
                    ufSgUf: ufMedicoRelacionado,
                    dataInicioPeriodo,
                    dataFinalPeriodo,
                    sgUfDestino: ufDestino,
                    sgUfOrigem: ufOrigem,
                    nuCrmOrigem: tipoVisto === 0 || localVisto === 1 ? this.valorOpcionalVisto(solicitacao.crmOrigem) : undefined
                }
            ]
        };

        if (deveVincularInteressado) {
            payload.idInteressado = solicitacao.requerenteId;
            payload.ufInteressado = ufRequerente;
        }

        return payload;
    }

    private async buscarContextoTramitacaoVisto(): Promise<{ uf: string; idSolicitante: number; idEstrutura: number } | null> {
        const ufToken = this.normalizarUfVisto(this.authService.ufDoUsuario());
        const userInfo = await this.authService.getUserInfo();
        const cpf = this.manterSomenteDigitosVisto(userInfo.cpf);
        const userId = this.valorOpcionalVisto(userInfo.userId);

        if (!ufToken || (!cpf && !userId)) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Nao foi possivel identificar o usuario logado para criar a tramitacao.' });
            return null;
        }

        try {
            const usuario = await this.localizarUsuarioLogadoVisto(cpf, ufToken, userId);
            if (!usuario) {
                this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Usuario logado nao localizado no SICOM.' });
                return null;
            }

            const uf = this.normalizarUfVisto(usuario.ufSolicitante) || ufToken;
            const estruturas = await firstValueFrom(this.usuarioService.listarSetores(usuario.id));
            const estrutura = estruturas.find((item) => (item.inStatus ?? '').trim().toUpperCase() === 'A') ?? estruturas[0];
            if (!estrutura?.idEstrutura) {
                this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Usuario logado sem estrutura organizacional vinculada.' });
                return null;
            }

            return {
                uf,
                idSolicitante: usuario.idSolicitante,
                idEstrutura: estrutura.idEstrutura
            };
        } catch (erro) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: this.extrairMensagemErro(erro) ?? 'Nao foi possivel carregar os dados do usuario logado.'
            });
            return null;
        }
    }

    private async localizarUsuarioLogadoVisto(cpf: string, uf: string, userId?: string): Promise<UsuarioResumoResponse | undefined> {
        if (cpf) {
            const usuariosPorCpf = await firstValueFrom(this.usuarioService.listar({ cpf, uf, inAtivo: 'S', page: 0, size: 1 }));
            const usuarioPorCpf = usuariosPorCpf.content?.[0];
            if (usuarioPorCpf) {
                return usuarioPorCpf;
            }
        }

        if (!userId) {
            return undefined;
        }

        const usuariosPorUserId = await firstValueFrom(this.usuarioService.listar({ userId, inAtivo: 'S', page: 0, size: 1 }));
        return usuariosPorUserId.content?.[0];
    }

    private montarDadosVistoCriado(solicitacao: NovoVistoSolicitacao, protocolo: VistoTemporarioSolicitacaoCadastroResponse, payload: VistoTemporarioSolicitacaoCadastroRequest): VistoTemporarioProtocoloSelecionado {
        return {
            idProtocolo: protocolo.idProtocolo,
            idSolicitante: payload.medicos[0]?.idSolicitante ?? solicitacao.solicitanteId,
            ufSgUf: payload.medicos[0]?.ufSgUf ?? payload.ufSolicitante,
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            tipoVisto: solicitacao.tipoVisto === '90_CORRIDOS' ? '90 dias corridos' : '90 dias fracionados',
            solicitante: solicitacao.solicitante,
            solicitanteCpfCnpj: solicitacao.solicitanteCpfCnpj,
            requerente: payload.idInteressado ? solicitacao.requerente : undefined,
            requerenteCpfCnpj: payload.idInteressado ? solicitacao.requerenteCpfCnpj : undefined,
            finalidade: solicitacao.finalidade,
            numeroDocumentoOrigem: payload.documentoOrigem,
            periodoInicio: solicitacao.periodoInicio,
            periodoFim: solicitacao.periodoFim,
            inSituacao: 'P',
            ufOrigem: solicitacao.ufOrigem,
            crmOrigem: solicitacao.crmOrigem,
            medicosRelacionados: [
                {
                    uf: payload.medicos[0]?.ufSgUf ?? payload.ufSolicitante,
                    medico: solicitacao.medicoRelacionado ?? solicitacao.solicitante,
                    cpf: solicitacao.medicoRelacionadoCpfCnpj ?? solicitacao.solicitanteCpfCnpj ?? '',
                    crm: solicitacao.medicoRelacionadoCrm ?? solicitacao.solicitanteCrm
                }
            ]
        };
    }

    private converterDataVistoParaIso(valor: string): string | null {
        const data = (valor ?? '').trim();
        const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(data);
        if (!partes) {
            return DateUtils.formatDateToApi(data);
        }

        const [, dia, mes, ano] = partes;
        return DateUtils.isDataValida(data) ? `${ano}-${mes}-${dia}` : null;
    }

    private normalizarUfVisto(valor?: string | null): string {
        const uf = (valor ?? '').trim().toUpperCase();
        return /^[A-Z]{2}$/.test(uf) ? uf : '';
    }

    private valorOpcionalVisto(valor?: string | null): string | undefined {
        const texto = (valor ?? '').toString().trim();
        return texto || undefined;
    }

    private manterSomenteDigitosVisto(valor?: string | null): string {
        return (valor ?? '').replace(/\D/g, '');
    }
}
